import React, { useMemo } from "react";
import DataTable, { createTheme } from "react-data-table-component";
import { useSelector } from "react-redux";
import { TableSkeleton } from "./Skeleton/TableSkeleton";

createTheme("darkTheme", {
  text: {
    primary: "#999999",
    secondary: "#999999",
  },
  background: {
    default: "transparent",
  },
  context: {
    background: "#232329",
    text: "#fff",
  },
  // button: {
  //     default: '#fff',
  //     hover: '#fff',
  //     focus: '#fff',
  //     disabled: '#eee',
  // },
  divider: {
    default: "#EEE",
  },
  action: {
    button: "#fff",
    hover: "#fff",
    disabled: "#fff",
  },
});
createTheme("lightTheme", {
  text: {
    primary: "#646777",
    secondary: "#646777",
  },
  background: {
    default: "transparent",
  },
  context: {
    background: "#fff",
    text: "#646777",
  },
  divider: {
    default: "#eee",
  },
  // button: {
  //     default: '#444',
  //     hover: '#444',
  //     focus: '#444',
  //     disabled: '#666',
  // },
  action: {
    button: "rgba(0,0,0,.54)",
    hover: "rgba(0,0,0,.08)",
    disabled: "rgba(0,0,0,.12)",
  },
});

const customStyles = {
  // headCells: {
  //   style: {
  //     position: 'sticky',
  //     right: 0,
  //     zIndex: 1,
  //     backgroundColor: '#fff',
  //     textAlign: 'center',
  //   },
  // },
  headRow: {
    style: {
      border: "none",
      fontWeight: "600",
      fontSize: "12px",
      paddingLeft: "0px",
    },
  },
  rows: {
    highlightOnHoverStyle: {
      borderRadius: "5px",
    },
  },
  pagination: {
    style: {
      border: "none",
    },
    pageButtonsStyle: {
      color: "#646777",
      fill: "#646777",
      borderRadius: "5px",
      marginLeft: "5px",

      '&[aria-disabled="true"]': {
        color: "#dddddd",
        fill: "#dddddd",
      },

      '&[aria-disabled="false"]': {
        color: "#646777",
        fill: "#646777",
      },
    },
  },
  cells: {
    style: {
      paddingTop: "10px",
      paddingRight: "10px",
      paddingBottom: "10px",
      fontSize: "12px",
      whiteSpace: "normal",
      wordBreak: "break-word",
      width: "auto",
    },
  },
};
const customDarkStyles = {
  // headCells: {
  //   style: {
  //     position: 'sticky',
  //     right: 0,
  //     zIndex: 1,
  //     backgroundColor: '#232329',
  //     textAlign: 'center',
  //   },
  // },
  headRow: {
    style: {
      border: "none",
      fontWeight: "600",
      fontSize: "12px",
      color: "#ffffff",
      paddingLeft: "0px",
    },
  },
  rows: {
    highlightOnHoverStyle: {
      backgroundColor: "#383838",
      borderRadius: "5px",
      color: "#999999",
    },
  },
  pagination: {
    style: {
      border: "none",
    },
    pageButtonsStyle: {
      color: "#fff",
      fill: "#fff",
      borderRadius: "5px",
      marginLeft: "5px",

      '&[aria-disabled="true"]': {
        color: "#646777",
        fill: "#646777",
      },

      '&[aria-disabled="false"]': {
        color: "#fff",
        fill: "#fff",
      },
    },
  },
  cells: {
    style: {
      paddingTop: "10px",
      paddingRight: "10px",
      paddingBottom: "10px",
      fontSize: "12px",
      whiteSpace: "normal",
      wordBreak: "break-word",
      width: "auto",
    },
  },
};

const CustomDataTable = ({
  columns = [],
  data = [],
  progressPending = false,
  paginationTotalRows,
  pagination = false,
  responsive = true,
  highlightOnHover = false,
  paginationServer = false,
  selectableRows = false,
  dense = false,
  onColumnOrderChange,
  onRowDoubleClicked,
  onChangePage,
  onChangeRowsPerPage,
  onRowClicked,
  onRowRightClick,
  onSort,
  onChange,
  onRowClick,
  paginationPerPage,
  onSelectedRowsChange,
  selectableRowDisabled,
  onRowMouseEnter,
  paginationDefaultPage,
  persistTableHead,
  sortServer = false,
  noHeader = false,
  selectableRowsSingle = false,
  defaultSortFieldId = "",
  defaultSortAsc = false,
  paginationRowsPerPageOptions = [10, 20, 30, 40, 50],
}) => {
  const theme = useSelector(state => state?.theme?.className);
  const memoizedStyles = useMemo(
    () => (theme === "dark" ? customDarkStyles : customStyles),
    [theme],
  );
  const memoizedTheme = useMemo(
    () => (theme === "dark" ? "darkTheme" : "lightTheme"),
    [theme],
  );

  // Process columns to add sticky styles if needed
  const processedColumns = columns.map((column, index) => {
    if (column.sticky) {
      // Calculate right position for sticky right columns
      let rightPosition = 0;
      if (column.sticky === "right") {
        // Find all sticky right columns after this one
        const rightColumns = columns
          .slice(index + 1)
          .filter(col => col.sticky === "right");
        // Calculate total width of columns after this one
        rightPosition = rightColumns.reduce(
          (acc, col) => acc + (col.width || 100),
          0,
        );
      }

      return {
        ...column,
        style: {
          ...column.style,
          position: "sticky",
          right: column.sticky === "right" ? rightPosition : undefined,
          left: column.sticky === "left" ? 0 : undefined,
          zIndex: 1,
          backgroundColor: theme === "dark" ? "#232329" : "#fff",
          boxShadow: "0 0 10px 0 rgba(0, 0, 0, 0.1)",
          paddingLeft: "10px",
        },
      };
    }
    return column;
  });

  return (
    <DataTable
      columns={columns}
      data={data}
      pagination={pagination}
      selectableRows={selectableRows}
      selectableRowsSingle={selectableRowsSingle}
      customStyles={memoizedStyles}
      theme={memoizedTheme}
      progressPending={progressPending}
      progressComponent={
        <TableSkeleton
          data-testid="loading-wrap"
          isLoading={progressPending}
          rowsCount={8}
          columnsCount={4}
          showPagination={true}
          headerCellWidth="150px"
          rowCellWidth="130px"
        />
      }
      noHeader={noHeader}
      responsive={responsive}
      highlightOnHover={highlightOnHover}
      paginationTotalRows={paginationTotalRows}
      onColumnOrderChange={onColumnOrderChange}
      paginationServer={paginationServer}
      onChangePage={onChangePage}
      paginationPerPage={paginationPerPage}
      onChangeRowsPerPage={onChangeRowsPerPage}
      onSelectedRowsChange={onSelectedRowsChange}
      selectableRowDisabled={selectableRowDisabled}
      onRowMouseEnter={onRowMouseEnter}
      paginationRowsPerPageOptions={paginationRowsPerPageOptions}
      paginationDefaultPage={paginationDefaultPage}
      persistTableHead={persistTableHead}
      sortServer={sortServer}
      onSort={onSort}
      defaultSortFieldId={defaultSortFieldId}
      defaultSortAsc={defaultSortAsc}
      onRowClicked={onRowClicked}
      onRowDoubleClicked={onRowDoubleClicked}
      dense={dense}
      onRowRightClick={onRowRightClick}
    />
  );
};
export default React.memo(CustomDataTable);
